import { Button, PanoramaViewer, type PanoramaPov } from '@citizen-walk/shared-ui';
import type { PhotoBlurRegion } from '@citizen-walk/shared-types';
import { useRef, useState, type PointerEvent } from 'react';
import {
  panoramaRectToRegions,
  rectFromDrag,
  regionScreenPoints,
  regionStrokeClass,
} from './photoPrivacyRegions';

/**
 * 360度写真の「写真のぼかし」の表示と編集 (docs/28 §10-1)。
 *
 * - ぼかした範囲を、見回している視点に合わせて写真の上に重ねて表示する (平面写真と同じ色分け:
 *   赤 = 自動で見つけた範囲、青 = 手で追加した範囲、点線 = 外した範囲)。
 * - `onAdd` を渡すと編集できる: 見回して範囲を正面に持ってきてから「範囲を描く」に切り替え、画面上を
 *   ドラッグする。描いた矩形はそのときの視点で全天球の多角形に変換して追加する。描くモードの間は
 *   視点が動かない。
 */
export interface PanoramaRegionEditorProps {
  src: string;
  regions: readonly PhotoBlurRegion[];
  /** 指定すると手での追加ができる (編集中)。 */
  onAdd?: (regions: PhotoBlurRegion[]) => void;
  disabled?: boolean;
  alt: string;
}

export function PanoramaRegionEditor({
  src,
  regions,
  onAdd,
  disabled = false,
  alt,
}: PanoramaRegionEditorProps) {
  const [pov, setPov] = useState<PanoramaPov>({ heading: 0, pitch: 0, hfov: 90 });
  const [drawing, setDrawing] = useState(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const [current, setCurrent] = useState<{ x: number; y: number; w: number; h: number } | null>(
    null,
  );
  const canDraw = Boolean(onAdd);

  const point = (e: PointerEvent<SVGSVGElement>) => {
    const b = e.currentTarget.getBoundingClientRect();
    return { x: (e.clientX - b.left) / b.width, y: (e.clientY - b.top) / b.height };
  };

  return (
    <div className="grid grid-cols-1 gap-2">
      <PanoramaViewer
        src={src}
        baseHeadingDeg={0}
        pov={pov}
        onPovChange={setPov}
        interactive={!drawing}
        className="h-80 w-full"
        alt={alt}
        renderOverlay={(view) => (
          <svg
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            className={`absolute inset-0 h-full w-full ${
              drawing ? 'pointer-events-auto cursor-crosshair touch-none' : 'pointer-events-none'
            }`}
            onPointerDown={(e) => {
              if (!drawing) return;
              start.current = point(e);
              e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={(e) => {
              if (drawing && start.current) setCurrent(rectFromDrag(start.current, point(e), 0));
            }}
            onPointerUp={(e) => {
              if (!drawing || !start.current) return;
              const r = rectFromDrag(start.current, point(e));
              start.current = null;
              setCurrent(null);
              if (r && onAdd) onAdd(panoramaRectToRegions(r, view));
            }}
            data-testid="panorama-region-overlay"
          >
            {regions.map((r, i) => {
              const pts = regionScreenPoints(r, view);
              return pts ? (
                <polygon
                  key={i}
                  points={pts}
                  vectorEffect="non-scaling-stroke"
                  className={regionStrokeClass(r)}
                />
              ) : null;
            })}
            {current && (
              <rect
                x={current.x}
                y={current.y}
                width={current.w}
                height={current.h}
                vectorEffect="non-scaling-stroke"
                className="fill-primary-600/10 stroke-primary-600 [stroke-width:2]"
              />
            )}
          </svg>
        )}
      />
      {canDraw && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={drawing ? 'primary' : 'secondary'}
            onClick={() => setDrawing((d) => !d)}
            disabled={disabled}
            data-testid="panorama-draw-toggle"
          >
            {drawing ? '見回しに戻る' : '範囲を描く'}
          </Button>
          <span className="text-[11px] text-slate-500">
            {drawing
              ? '画面の上をドラッグすると、その範囲にぼかしを追加します。'
              : '見回してぼかしたい所を正面に持ってきてから「範囲を描く」を押してください。'}
          </span>
        </div>
      )}
    </div>
  );
}
